import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IdentityApiError, identityApi, type AuthenticatorDto, type ChangePasswordPayload, type SignUpPayload, type LoginPayload, type SessionResponse, type UpdateProfilePayload, type TwoFactorLoginPayload, type MfaStatusResponse, type MfaPolicyResponse, type PasskeyDto } from "./identityApi";
import { createPasskey, getPasskeyAssertion } from "@/lib/webauthn";

// Email-first SSO discovery — a mutation (not a query) because it's triggered explicitly
// (on blur / before submit), not driven by a stable query key worth caching.
export function useSamlProviders() {
    return useMutation({
        mutationFn: (email: string) => identityApi.samlProviders(email)
    });
}

const keys = {
    session: ["session"] as const
};

export function useSession() {
    return useQuery<SessionResponse>({
        queryKey: keys.session,
        queryFn: identityApi.session,
        staleTime: 0,
        retry: false
    });
}

export function useLogin() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: LoginPayload) => identityApi.login(payload),
        // Only cache a session when login fully succeeded; a 2FA challenge
        // returns session=null and is finished by useLoginTwoFactor.
        onSuccess: (resp) => {
            if (resp.session) qc.setQueryData(keys.session, resp.session);
        }
    });
}

export function useLoginTwoFactor() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: TwoFactorLoginPayload) => identityApi.loginTwoFactor(payload),
        onSuccess: (resp) => {
            if (resp.session) qc.setQueryData(keys.session, resp.session);
        }
    });
}

export function useMfaPolicy(enabled = true) {
    return useQuery<MfaPolicyResponse>({
        queryKey: ["mfa-policy"],
        queryFn: identityApi.mfaPolicy,
        enabled,
        retry: false
    });
}

export function useMfaStatus(enabled = true) {
    return useQuery<MfaStatusResponse>({
        queryKey: ["mfa-status"],
        queryFn: identityApi.mfaStatus,
        enabled,
        staleTime: 0,
        retry: false
    });
}

export function useEnrollMfa() {
    return useMutation({ mutationFn: () => identityApi.mfaEnroll() });
}

export function useEnableMfa() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (code: string) => identityApi.mfaEnable(code),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["mfa-status"] })
    });
}

export function useDisableMfa() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => identityApi.mfaDisable(),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["mfa-status"] })
    });
}

// ---- Passkeys (WebAuthn) ----

export function usePasskeys(enabled = true) {
    return useQuery<PasskeyDto[]>({
        queryKey: ["passkeys"],
        queryFn: identityApi.passkeyList,
        enabled,
        retry: false
    });
}

export function useRegisterPasskey() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const options = await identityApi.passkeyRegisterBegin();
            const raw = await createPasskey(options as never);
            return identityApi.passkeyRegisterFinish(raw);
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["passkeys"] })
    });
}

export function useDeletePasskey() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => identityApi.passkeyDelete(id),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["passkeys"] })
    });
}

// ---- Athavita Authenticator (phone sign-in approval) ----

export function useAuthenticators(enabled = true) {
    return useQuery<AuthenticatorDto[]>({
        queryKey: ["authenticators"],
        queryFn: identityApi.authenticators,
        enabled,
        retry: false
    });
}

export function useSetAuthenticatorPasswordless() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ deviceId, passwordless }: { deviceId: string; passwordless: boolean }) =>
            identityApi.setAuthenticatorPasswordless(deviceId, passwordless),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["authenticators"] })
    });
}

export function useRemoveAuthenticator() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (deviceId: string) => identityApi.removeAuthenticator(deviceId),
        // Removing the last phone can turn two-factor off, so refresh its status too.
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["authenticators"] });
            void qc.invalidateQueries({ queryKey: ["mfa-status"] });
        }
    });
}

export function useLoginWithPasskey() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const options = await identityApi.passkeyLoginBegin();
            const raw = await getPasskeyAssertion(options as never);
            return identityApi.passkeyLoginFinish(raw);
        },
        onSuccess: (resp) => {
            if (resp.session) qc.setQueryData(["session"], resp.session);
        }
    });
}

export function useSignUp() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: SignUpPayload) => identityApi.signup(payload),
        onSuccess: (session) => qc.setQueryData(keys.session, session)
    });
}

export function useLogout() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => identityApi.logout(),
        onSuccess: () =>
            qc.setQueryData<SessionResponse>(keys.session, {
                isAuthenticated: false,
                userId: null,
                email: null,
                displayName: null,
                tenantId: null,
                roles: []
            })
    });
}

export function useChangePassword() {
    return useMutation({
        mutationFn: (payload: ChangePasswordPayload) => identityApi.changePassword(payload)
    });
}

export function useUpdateProfile() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: UpdateProfilePayload) => identityApi.updateProfile(payload),
        onSuccess: (_, vars) => {
            // Patch the cached session so the welcome banner reflects the
            // new name immediately, without a roundtrip.
            qc.setQueryData<SessionResponse>(keys.session, (prev) =>
                prev ? { ...prev, displayName: vars.displayName } : prev);
        }
    });
}

export { IdentityApiError };
